import React from 'react';
import { motion } from 'framer-motion';
import Lottie from 'lottie-react';
import beerpongCupData from '../../assets/lottie/beerpong_cup.json';
import CountingNumber from '../CountingNumber';
import { scrollToAppPromotion } from '../../utils/scrollToApp';
import { EloData } from '../../types/api.types';

interface PlayerSetStatisticsProps {
  playerData: EloData;
  t: {
    setStatistics: string;
    setStatsDescription: string;
    matchesWithScores: string;
    matches: string;
    losses: string;
    wins: string;
    winRateWhenWonFirst: string;
    winRateWhenLostFirst: string;
    reverseSweeps: string;
    wonAfterLosingFirst: string;
    unlockMore: string;
  };
}

/**
 * PlayerSetStatistics Component
 * Displays set statistics including scorelines, first set advantage, and comeback stats
 */
const PlayerSetStatistics: React.FC<PlayerSetStatisticsProps> = ({
  playerData,
  t
}) => {
  if (!playerData.set_stats || playerData.set_stats.total_matches_with_scores === 0) {
    return null;
  }

  return (
    <div className="max-w-6xl mx-auto mb-16">
      {/* Section Header */}
      <motion.div
        className="mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}>
        <div className="text-center mb-10">
          {/* Lottie above title */}
          <div style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '-80px',
            marginTop: '-100px',
            overflow: 'visible'
          }}>
            <motion.div
              style={{
                width: 'min(450px, calc(100vw - 2rem))',
                height: 'min(450px, calc(100vw - 2rem))',
                flexShrink: 0
              }}
              initial={{ opacity: 0, scale: 0.8, x: 0 }}
              animate={{ opacity: 1, scale: 1, x: 8 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Lottie
                animationData={beerpongCupData}
                loop
                style={{ width: '100%', height: '100%' }}
                rendererSettings={{
                  preserveAspectRatio: 'xMidYMid meet'
                }}
              />
            </motion.div>
          </div>
          <h2 className="text-4xl font-light text-gray-900 dark:text-gray-100 mb-3 transition-colors duration-300">{t.setStatistics}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">
          {t.setStatsDescription} • {playerData.set_stats.total_matches_with_scores} {t.matchesWithScores}
          </p>
        </div>
      </motion.div>

      {/* Donut Chart with Set Distribution - Integrated Card */}
      <motion.div
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 lg:p-8 shadow-lg mb-4 transition-colors duration-300"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}>
        {/* Donut Chart - Simple and Clean */}
        <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-16">
          {/* Chart */}
          <div className="relative inline-flex items-center justify-center">
            <svg
              viewBox="0 0 256 256"
              className="w-64 h-64 lg:w-80 lg:h-80 transform -rotate-90">
            {/* Background circle */}
            <circle
              cx="128"
              cy="128"
              r="90"
              stroke="currentColor"
              className="text-gray-200 dark:text-gray-700"
              strokeWidth="28"
              fill="none"
            />

            {(() => {
              if (!playerData.set_stats) return null;

              const centerX = 128;
              const centerY = 128;
              const radius = 90;
              const circumference = 2 * Math.PI * radius;

              return (
                <>

                  {/* Animated segments */}
                  {(() => {
                    const segments = [
                { key: "0-3_losses", value: playerData.set_stats.scorelines["0-3_losses"], color: "#dc2626", darkColor: "#b91c1c" },
                { key: "1-3_losses", value: playerData.set_stats.scorelines["1-3_losses"], color: "#ef4444", darkColor: "#dc2626" },
                { key: "2-3_losses", value: playerData.set_stats.scorelines["2-3_losses"], color: "#f87171", darkColor: "#ef4444" },
                { key: "3-2_wins", value: playerData.set_stats.scorelines["3-2_wins"], color: "#34d399", darkColor: "#10b981" },
                { key: "3-1_wins", value: playerData.set_stats.scorelines["3-1_wins"], color: "#10b981", darkColor: "#059669" },
                { key: "3-0_wins", value: playerData.set_stats.scorelines["3-0_wins"], color: "#059669", darkColor: "#047857" }
              ];

              let currentOffset = 0;
              const gapSize = 6; // Gap between segments for clean separation

              return segments.map((segment, index) => {
                if (segment.value === 0) return null;

                const percentage = (segment.value / playerData.set_stats!.total_matches_with_scores) * 100;
                const segmentLength = Math.max(0, (percentage / 100) * circumference - gapSize);
                const offset = currentOffset;
                currentOffset += segmentLength + gapSize;

                return (
                  <motion.circle
                    key={segment.key}
                    cx={centerX}
                    cy={centerY}
                    r={radius}
                    stroke={segment.color}
                    strokeWidth="28"
                    fill="none"
                    strokeDasharray={`${segmentLength} ${circumference - segmentLength}`}
                    strokeDashoffset={-offset}
                    strokeLinecap="butt"
                    initial={{ strokeDasharray: `0 ${circumference}` }}
                    animate={{ strokeDasharray: `${segmentLength} ${circumference - segmentLength}` }}
                    transition={{ duration: 1.5, delay: 0.5 + index * 0.1, ease: "easeOut" }}
                    className="dark:hidden"
                  />
                );
              }).concat(segments.map((segment, index) => {
                if (segment.value === 0) return null;

                // Recalculate for dark mode with same gaps
                let darkOffset = 0;
                for (let i = 0; i < index; i++) {
                  if (segments[i].value > 0) {
                    const pct = (segments[i].value / playerData.set_stats!.total_matches_with_scores) * 100;
                    darkOffset += Math.max(0, (pct / 100) * circumference - gapSize) + gapSize;
                  }
                }

                const percentage = (segment.value / playerData.set_stats!.total_matches_with_scores) * 100;
                const segmentLength = Math.max(0, (percentage / 100) * circumference - gapSize);

                return (
                  <motion.circle
                    key={`${segment.key}-dark`}
                    cx={centerX}
                    cy={centerY}
                    r={radius}
                    stroke={segment.darkColor}
                    strokeWidth="28"
                    fill="none"
                    strokeDasharray={`${segmentLength} ${circumference - segmentLength}`}
                    strokeDashoffset={-darkOffset}
                    strokeLinecap="butt"
                    initial={{ strokeDasharray: `0 ${circumference}` }}
                    animate={{ strokeDasharray: `${segmentLength} ${circumference - segmentLength}` }}
                    transition={{ duration: 1.5, delay: 0.5 + index * 0.1, ease: "easeOut" }}
                    className="hidden dark:block"
                  />
                );
                  }));
                  })()}
                </>
              );
            })()}
          </svg>

          {/* Center text overlay */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <div className="text-5xl lg:text-6xl font-bold text-gray-900 dark:text-gray-100">
              <CountingNumber target={playerData.set_stats?.total_matches_with_scores || 0} duration={2} delay={800} decimals={0} />
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t.matches}
            </div>
          </div>
        </div>

        {/* Compact Legend - Grouped by Losses (Red) and Wins (Green) */}
        <div className="flex gap-6 mx-auto lg:mx-0">
          {/* Losses Group (Red) */}
          <div className="space-y-3">
            {playerData.set_stats.scorelines["0-3_losses"] > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-rose-600 dark:bg-rose-700 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">0-3</span>
                <span className="text-sm font-bold text-gray-900 dark:text-gray-100 ml-auto">{playerData.set_stats.scorelines["0-3_losses"]}</span>
              </div>
            )}
            {playerData.set_stats.scorelines["1-3_losses"] > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-rose-500 dark:bg-rose-600 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">1-3</span>
                <span className="text-sm font-bold text-gray-900 dark:text-gray-100 ml-auto">{playerData.set_stats.scorelines["1-3_losses"]}</span>
              </div>
            )}
            {playerData.set_stats.scorelines["2-3_losses"] > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-rose-400 dark:bg-rose-500 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">2-3</span>
                <span className="text-sm font-bold text-gray-900 dark:text-gray-100 ml-auto">{playerData.set_stats.scorelines["2-3_losses"]}</span>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="w-px bg-gray-200 dark:bg-gray-700" />

          {/* Wins Group (Green) */}
          <div className="space-y-3">
            {playerData.set_stats.scorelines["3-2_wins"] > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-400 dark:bg-emerald-500 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">3-2</span>
                <span className="text-sm font-bold text-gray-900 dark:text-gray-100 ml-auto">{playerData.set_stats.scorelines["3-2_wins"]}</span>
              </div>
            )}
            {playerData.set_stats.scorelines["3-1_wins"] > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500 dark:bg-emerald-600 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">3-1</span>
                <span className="text-sm font-bold text-gray-900 dark:text-gray-100 ml-auto">{playerData.set_stats.scorelines["3-1_wins"]}</span>
              </div>
            )}
            {playerData.set_stats.scorelines["3-0_wins"] > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-600 dark:bg-emerald-700 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">3-0</span>
                <span className="text-sm font-bold text-gray-900 dark:text-gray-100 ml-auto">{playerData.set_stats.scorelines["3-0_wins"]}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      </motion.div>

      {/* First Set Advantage - Win Rates */}
      {playerData.set_stats.first_set && (
        <div className="grid grid-cols-2 gap-4 mb-4">
          {/* Won First Set Win Rate */}
          <motion.div
            className="bg-white dark:bg-gray-800 rounded-2xl p-4 lg:p-6 shadow-lg transition-colors duration-300"
            initial={{ opacity: 0, scale: 0.9, y: 0 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.6, type: "spring", stiffness: 100 }}>
            <div className="text-center">
              <div className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-4 transition-colors duration-300">{t.winRateWhenWonFirst}</div>
              <div className="relative inline-flex items-center justify-center mb-4">
                <svg className="w-24 h-24 lg:w-32 lg:h-32 transform -rotate-90">
                  <circle cx={window.innerWidth < 1024 ? 48 : 64} cy={window.innerWidth < 1024 ? 48 : 64} r={window.innerWidth < 1024 ? 40 : 56} stroke="currentColor" className="text-gray-200 dark:text-gray-700" strokeWidth="8" fill="none" />
                  <motion.circle
                    cx={window.innerWidth < 1024 ? 48 : 64} cy={window.innerWidth < 1024 ? 48 : 64} r={window.innerWidth < 1024 ? 40 : 56}
                    stroke="currentColor"
                    className={
                      (playerData.set_stats.first_set.match_win_rate_when_won || 0) >= 60
                        ? 'text-[#059669]'
                        : (playerData.set_stats.first_set.match_win_rate_when_won || 0) >= 40
                        ? 'text-[#F59E0B]'
                        : 'text-[#C5283D]'
                    }
                    strokeWidth="8" fill="none"
                    strokeDasharray={`${2 * Math.PI * (window.innerWidth < 1024 ? 40 : 56)}`}
                    strokeDashoffset={`${2 * Math.PI * (window.innerWidth < 1024 ? 40 : 56)}`}
                    strokeLinecap="round"
                    initial={{
                      strokeDashoffset: `${2 * Math.PI * (window.innerWidth < 1024 ? 40 : 56)}`
                    }}
                    animate={{
                      strokeDashoffset: `${2 * Math.PI * (window.innerWidth < 1024 ? 40 : 56) * (1 - (playerData.set_stats.first_set.match_win_rate_when_won || 0) / 100)}`
                    }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className={`text-3xl lg:text-4xl font-bold ${
                    (playerData.set_stats.first_set.match_win_rate_when_won || 0) >= 60
                      ? 'text-[#059669]'
                      : (playerData.set_stats.first_set.match_win_rate_when_won || 0) >= 40
                      ? 'text-[#F59E0B]'
                      : 'text-[#C5283D]'
                  }`}>
                    <CountingNumber target={playerData.set_stats.first_set.match_win_rate_when_won || 0} duration={2} delay={1100} decimals={0} suffix="%" />
                  </div>
                </div>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 transition-colors duration-300">
                ({playerData.set_stats.first_set.wins}/{playerData.set_stats.first_set.total})
              </div>
            </div>
          </motion.div>

          {/* Lost First Set Win Rate */}
          <motion.div
            className="bg-white dark:bg-gray-800 rounded-2xl p-4 lg:p-6 shadow-lg transition-colors duration-300"
            initial={{ opacity: 0, scale: 0.9, y: 0 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.6, type: "spring", stiffness: 100 }}>
            <div className="text-center">
              <div className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-4 transition-colors duration-300">{t.winRateWhenLostFirst}</div>
              <div className="relative inline-flex items-center justify-center mb-4">
                <svg className="w-24 h-24 lg:w-32 lg:h-32 transform -rotate-90">
                  <circle cx={window.innerWidth < 1024 ? 48 : 64} cy={window.innerWidth < 1024 ? 48 : 64} r={window.innerWidth < 1024 ? 40 : 56} stroke="currentColor" className="text-gray-200 dark:text-gray-700" strokeWidth="8" fill="none" />
                  <motion.circle
                    cx={window.innerWidth < 1024 ? 48 : 64} cy={window.innerWidth < 1024 ? 48 : 64} r={window.innerWidth < 1024 ? 40 : 56}
                    stroke="currentColor"
                    className={
                      (playerData.set_stats.first_set.match_win_rate_when_lost || 0) >= 40
                        ? 'text-[#059669]'
                        : (playerData.set_stats.first_set.match_win_rate_when_lost || 0) >= 25
                        ? 'text-[#F59E0B]'
                        : 'text-[#C5283D]'
                    }
                    strokeWidth="8" fill="none"
                    strokeDasharray={`${2 * Math.PI * (window.innerWidth < 1024 ? 40 : 56)}`}
                    strokeDashoffset={`${2 * Math.PI * (window.innerWidth < 1024 ? 40 : 56)}`}
                    strokeLinecap="round"
                    initial={{ strokeDashoffset: `${2 * Math.PI * (window.innerWidth < 1024 ? 40 : 56)}` }}
                    animate={{
                      strokeDashoffset: `${2 * Math.PI * (window.innerWidth < 1024 ? 40 : 56) * (1 - (playerData.set_stats.first_set.match_win_rate_when_lost || 0) / 100)}`
                    }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className={`text-3xl lg:text-4xl font-bold ${
                    (playerData.set_stats.first_set.match_win_rate_when_lost || 0) >= 40
                      ? 'text-[#059669]'
                      : (playerData.set_stats.first_set.match_win_rate_when_lost || 0) >= 25
                      ? 'text-[#F59E0B]'
                      : 'text-[#C5283D]'
                  }`}>
                    <CountingNumber target={playerData.set_stats.first_set.match_win_rate_when_lost || 0} duration={2} delay={1130} decimals={0} suffix="%" />
                  </div>
                </div>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 transition-colors duration-300">
                ({Math.round((playerData.set_stats.first_set.total - playerData.set_stats.first_set.wins) * (playerData.set_stats.first_set.match_win_rate_when_lost || 0) / 100)}/{playerData.set_stats.first_set.total - playerData.set_stats.first_set.wins})
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Comeback Stats - Only show if player has comebacks */}
      {(playerData.set_stats.comebacks.reverse_sweeps > 0 || playerData.set_stats.comebacks.won_after_losing_first > 0) && (
        <div className={`grid gap-4 ${
          playerData.set_stats.comebacks.reverse_sweeps > 0 && playerData.set_stats.comebacks.won_after_losing_first > 0
            ? 'grid-cols-2'
            : 'grid-cols-1'
        }`}>
          {/* Reverse Sweeps - only show if > 0 */}
          {playerData.set_stats.comebacks.reverse_sweeps > 0 && (
            <motion.div
              className="bg-white dark:bg-gray-800 rounded-2xl p-4 lg:p-6 shadow-lg transition-colors duration-300"
              initial={{ opacity: 0, scale: 0.9, y: 0 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.6, type: "spring", stiffness: 100 }}>
              <div className="text-center">
                <div className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-4 transition-colors duration-300">{t.reverseSweeps}</div>
                <div className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-gray-100 transition-colors duration-300">
                  <CountingNumber target={playerData.set_stats.comebacks.reverse_sweeps} duration={2} delay={950} decimals={0} />
                </div>
              </div>
            </motion.div>
          )}

          {/* Won After Losing First Set - only show if > 0 */}
          {playerData.set_stats.comebacks.won_after_losing_first > 0 && (
            <motion.div
              className="bg-white dark:bg-gray-800 rounded-2xl p-4 lg:p-6 shadow-lg transition-colors duration-300"
              initial={{ opacity: 0, scale: 0.9, y: 0 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.6, type: "spring", stiffness: 100 }}>
              <div className="text-center">
                <div className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-4 transition-colors duration-300">{t.wonAfterLosingFirst}</div>
                <div className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-gray-100 transition-colors duration-300">
                  <CountingNumber target={playerData.set_stats.comebacks.won_after_losing_first} duration={2} delay={1000} decimals={0} />
                </div>
              </div>
            </motion.div>
          )}
        </div>
      )}

      {/* Unlock More Stats CTA */}
      <div className="mt-6 text-center">
        <button
          onClick={() => scrollToAppPromotion()}
          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-300"
        >
          {t.unlockMore} →
        </button>
      </div>
    </div>
  );
};

export default PlayerSetStatistics;
